import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Boxes, Truck, Check, Gift } from "lucide-react";
import { useApp } from "../context/AppContext";
import { CATEGORIES, SECTORS, PRODUCTS, TVA } from "../data/mock";
import Marquee from "../components/Marquee";
import AdBanner from "../components/AdBanner";

const fmtDA = (n) => new Intl.NumberFormat("fr-DZ").format(Math.round(n));
const FEATURED = ["hamoud-assort-1l", "bimo-maliya", "danone-yomi", "huile-5l"];

const Home = () => {
  const { t, tf, lang, catalog, isPro } = useApp();
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;
  const engageIcons = [ShieldCheck, Boxes, Truck];

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-cream to-white" />
        <div className="absolute top-10 ltr:left-0 rtl:right-0 -z-10 font-display font-black text-[22vw] leading-none select-none pointer-events-none" style={{ color: "rgba(15,31,92,0.045)" }}>Ssuq</div>

        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rise">
            <span className="inline-flex items-center gap-2 bg-navy/5 text-navy font-display font-semibold text-xs px-4 py-1.5 rounded-full">
              <span className="w-2 h-2 rounded-full bg-orange" /> {t("hero_badge")}
            </span>
            <h1 className="font-display font-black text-5xl md:text-6xl xl:text-7xl leading-[0.98] mt-5 text-navy">
              {t("hero_title_1")}<br /><span className="text-orange">{t("hero_title_2")}</span>
            </h1>
            <p className="mt-6 text-gray-600 text-lg leading-relaxed max-w-xl">{t("hero_sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/catalogue" className="btn-orange font-display font-semibold rounded-full px-7 py-3.5 inline-flex items-center gap-2">{t("hero_cta_1")} <Arrow size={18} /></Link>
              <Link to="/inscription" className="btn-outline font-display font-semibold rounded-full px-7 py-3.5">{t("hero_cta_2")}</Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              {[["B2B", t("stat_b2b")], ["48–72h", t("stat_delay")], ["120+", t("stat_vendors")]].map(([n, l], i) => (
                <div key={i}>
                  <div className="font-display font-extrabold text-2xl text-navy">{n}</div>
                  <div className="text-xs text-gray-500 mt-1 leading-tight">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative rise">
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/5] max-w-md ltr:ml-auto rtl:mr-auto">
              <img src="https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Chef" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent" />
              <div className="absolute bottom-5 ltr:left-5 rtl:right-5 ltr:right-5 rtl:left-5 text-white">
                <p className="font-serif-d text-xl md:text-2xl">{t("hero_quote")}</p>
                <p className="text-white/70 text-sm mt-1">{t("hero_quote_sub")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* PUBLICITÉ */}
      <AdBanner />

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-5 py-20 md:py-28">
        <p className="font-display font-semibold text-orange text-sm uppercase tracking-wider">{t("manifesto_label")}</p>
        <h2 className="font-serif-d text-3xl md:text-5xl text-navy mt-4 max-w-4xl leading-tight">{t("manifesto_title")}</h2>
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {t("engage").map((e, i) => {
            const Icon = engageIcons[i];
            return (
              <div key={i} className="border-t-2 border-navy/10 pt-6">
                <div className="flex items-center justify-between">
                  <span className="font-display font-black text-4xl text-navy/15">{e.n}</span>
                  <div className="w-11 h-11 rounded-full bg-orange/10 flex items-center justify-center"><Icon size={20} className="text-orange" /></div>
                </div>
                <h3 className="font-display font-bold text-xl text-navy mt-4">{e.t}</h3>
                <p className="text-gray-600 mt-2 leading-relaxed text-[15px]">{e.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy">{t("cat_label")}</h2>
            <Link to="/catalogue" className="font-display font-semibold text-orange inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">{t("cat_all")} <Arrow size={16} /></Link>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-5">
            {CATEGORIES.map((c) => (
              <Link to={`/catalogue?cat=${c.id}`} key={c.id} className="cat-tile group relative rounded-2xl overflow-hidden aspect-[4/3] shadow-sm">
                <img src={c.image} alt={tf(c.name)} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/85 via-navy-900/20 to-transparent" />
                <div className="absolute bottom-4 ltr:left-4 rtl:right-4 ltr:right-4 rtl:left-4">
                  <h3 className="font-display font-bold text-white text-lg leading-tight">{tf(c.name)}</h3>
                  <span className="text-white/80 text-xs inline-flex items-center gap-1 mt-1 group-hover:text-orange transition-colors">{t("cat_see")} <Arrow size={13} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-5 py-20 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="font-display font-semibold text-orange text-sm uppercase tracking-wider">{t("feat_label")}</p>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy mt-2">{t("feat_title")}</h2>
          </div>
          <Link to="/catalogue" className="font-display font-semibold text-orange inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">{t("feat_cta")} <Arrow size={16} /></Link>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {catalog.slice(0, 4).map((p) => {
            if (!p) return null;
            return (
              <Link to="/catalogue" key={p.id} className="prod-card bg-white rounded-2xl overflow-hidden border border-gray-100 flex flex-col">
                <div className="relative h-44 overflow-hidden bg-cream">
                  <img src={p.image} alt={tf(p.name)} className="w-full h-full object-cover" loading="lazy" />
                  <span className="absolute top-3 ltr:left-3 rtl:right-3 bg-navy text-white text-[11px] font-display font-semibold px-2.5 py-1 rounded-full">{tf(p.vendorType)}</span>
                  {p.promo && <span className="absolute bottom-3 ltr:left-3 rtl:right-3 bg-orange text-white text-[11px] font-display font-bold px-2.5 py-1 rounded-full">{p.promo}</span>}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-display font-bold text-navy text-sm leading-snug line-clamp-2 min-h-[40px]">{tf(p.name)}</h3>
                  <div className="mt-3 font-display">
                    {isPro && p.priceHT != null ? (
                      <>
                        <span className="text-xl font-extrabold text-navy">{fmtDA(p.priceHT)}</span>
                        <span className="text-navy font-bold text-sm"> {t("currency")}</span>
                        <span className="text-[11px] text-gray-400 ltr:ml-1 rtl:mr-1">{t("ht_unit")}{tf(p.unit)}</span>
                      </>
                    ) : (
                      <span className="text-sm font-semibold text-orange">{t("price_locked")}</span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* PROMO BANNER — Prima & Hamoud */}
      <section className="max-w-7xl mx-auto px-5 py-6">
        <div className="relative overflow-hidden rounded-[2rem] bg-navy text-white p-8 md:p-12 grid lg:grid-cols-2 gap-10 items-center">
          <div className="absolute -top-16 ltr:-right-16 rtl:-left-16 w-64 h-64 rounded-full bg-orange/20 blur-3xl pointer-events-none" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 bg-orange text-white font-display font-bold text-xs px-4 py-1.5 rounded-full uppercase tracking-wider">
              {t("promo_label")} · {t("promo_badge")}
            </span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl mt-4 leading-tight">{t("promo_title")}</h2>
            <p className="text-white/70 mt-3 max-w-md leading-relaxed">{t("promo_sub")}</p>
            <Link to="/catalogue?cat=boissons" className="btn-orange font-display font-semibold rounded-full px-7 py-3.5 inline-flex items-center gap-2 mt-6">
              {t("promo_cta")} <Arrow size={18} />
            </Link>
          </div>
          <div className="relative grid grid-cols-2 gap-4">
            {[
              { label: "Prima", img: "https://images.unsplash.com/photo-1566846128021-b940b0eec910?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
              { label: "Hamoud Boualem", img: "/brands/hamoud.jpg" }
            ].map((b) => (
              <div key={b.label} className="relative rounded-2xl overflow-hidden aspect-[3/4] bg-white/5 border border-white/10">
                <img src={b.img} alt={b.label} className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute top-3 ltr:left-3 rtl:right-3 bg-orange text-white text-[11px] font-display font-bold px-2.5 py-1 rounded-full">{t("promo_badge")}</span>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy-900/90 to-transparent p-4">
                  <span className="font-display font-bold text-white">{b.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* DELIVERY */}
      <section className="max-w-7xl mx-auto px-5 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <div className="rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/3]">
            <img src="https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Entrepôt" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="absolute -bottom-6 ltr:-right-4 rtl:-left-4 w-44 md:w-52 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
            <img src="https://images.unsplash.com/photo-1606824722920-4c652a70f348?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" alt="Supérette" className="w-full h-32 md:h-36 object-cover" loading="lazy" />
          </div>
        </div>
        <div>
          <p className="font-display font-semibold text-orange text-sm uppercase tracking-wider">{t("deliv_label")}</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy mt-3">{t("deliv_title")}</h2>
          <p className="text-gray-600 mt-4 leading-relaxed">{t("deliv_sub")}</p>
          <div className="mt-8 space-y-5">
            {t("deliv_items").map((d, i) => (
              <div key={i} className="flex gap-4 items-start">
                <span className="font-display font-black text-2xl text-orange/70 shrink-0 w-10">{d.n}</span>
                <div>
                  <h4 className="font-display font-bold text-navy">{d.t}</h4>
                  <p className="text-gray-600 text-sm mt-0.5">{d.d}</p>
                </div>
              </div>
            ))}
          </div>
          <Link to="/inscription" className="btn-navy font-display font-semibold rounded-full px-7 py-3.5 inline-flex items-center gap-2 mt-8">{t("deliv_cta")} <Arrow size={18} /></Link>
        </div>
      </section>

      {/* VENDOR / SELL */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="font-display font-semibold text-orange text-sm uppercase tracking-wider">{t("vendor_label")}</p>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl mt-3">{t("vendor_title")}</h2>
            <p className="text-white/70 mt-4 leading-relaxed max-w-lg">{t("vendor_sub")}</p>
            <ul className="mt-7 space-y-3">
              {t("vendor_points").map((p, i) => (
                <li key={i} className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-orange flex items-center justify-center shrink-0"><Check size={14} /></span><span className="text-white/90">{p}</span></li>
              ))}
            </ul>
            <Link to="/inscription" className="btn-orange font-display font-semibold rounded-full px-7 py-3.5 inline-flex items-center gap-2 mt-8">{t("nav_compte")} <Arrow size={18} /></Link>
          </div>
          <div>
            <div className="rounded-[1.75rem] overflow-hidden shadow-2xl aspect-[16/11] relative">
              <img src="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Alimentation générale" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-transparent to-transparent" />
              <span className="absolute bottom-4 ltr:left-4 rtl:right-4 bg-white/95 text-navy font-display font-bold text-sm px-4 py-2 rounded-full">
                {lang === "ar" ? SECTORS[1].ar : SECTORS[1].fr}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              {SECTORS.map((s, i) => (
                <div key={s.id} className={`rounded-2xl p-4 ${i % 2 === 0 ? "bg-white/5" : "bg-orange/10"} border border-white/10`}>
                  <div className="font-display font-black text-2xl text-orange">0{i + 1}</div>
                  <div className="font-display font-semibold text-sm mt-1">{lang === "ar" ? s.ar : s.fr}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 py-20 md:py-28 text-center">
        <h2 className="font-display font-extrabold text-3xl md:text-5xl text-navy max-w-3xl mx-auto leading-tight">{t("cta_title")}</h2>
        <p className="text-gray-600 mt-4 max-w-xl mx-auto">{t("cta_sub")}</p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/inscription" className="btn-orange font-display font-semibold rounded-full px-7 py-3.5">{t("cta_1")}</Link>
          <Link to="/catalogue" className="btn-outline font-display font-semibold rounded-full px-7 py-3.5">{t("cta_2")}</Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
